/**
 * Canonical devig snapshot — single source of truth for:
 * - UnifiedBetCard (pass priceOverride when user picks a specific book; else best price)
 * - getCanonicalSortEv / results sort (omit priceOverride → best price across bookOdds)
 *
 * Peers must be supplied for opposite-side consensus when backend devig fields are absent.
 */

import * as betMath from './betMath.js';

const extractPrice = (bookOddsValue) => {
    if (bookOddsValue === null || bookOddsValue === undefined) return null;
    if (typeof bookOddsValue === 'object' && bookOddsValue.price !== undefined) {
        return bookOddsValue.price;
    }
    if (typeof bookOddsValue === 'number') return bookOddsValue;
    return null;
};

/**
 * The bookOdds key whose entry is `bookData` (same object), or null. The SGP
 * leave-one-out drops this book; with null, betMath.sgpStampInput drops the
 * consensus book with the same price instead.
 */
function bookNameForData(raw, bookData) {
    const bo = raw?.bookOdds;
    if (!bo || typeof bo !== 'object' || bookData == null) return null;
    for (const [bk, bd] of Object.entries(bo)) if (bd === bookData) return bk;
    return null;
}

/** Same key as SgpFlowScreen getMarketPeers / marketPeersMap */
export function getMarketKeyForPeers(opt) {
    const fixtureId = opt?.fixture_id || opt?.raw?.fixture_id || opt?._fixtureId || '';
    const market = opt?.market || opt?.raw?.market || '';
    const points = opt?.points || opt?.raw?.points || '';
    return `${fixtureId}_${market}_${points}`.toLowerCase();
}

export function buildMarketPeersMap(options) {
    const map = {};
    if (!Array.isArray(options)) return map;
    for (const opt of options) {
        const k = getMarketKeyForPeers(opt);
        if (!map[k]) map[k] = [];
        map[k].push(opt);
    }
    return map;
}

/**
 * Exchange lay feeds and backend-flagged single-book price outliers are not
 * prices to recommend (functions/evCleanPerBook.js sanitizeItemBooks): they
 * never become the best price, the best EV, or the preferred book.
 */
export function isUnbackableBookData(bd, bookName = '') {
    if (bd && typeof bd === 'object' && bd._price_outlier === true) return true;
    return isLayBookName(bookName);
}

/** Exchange lay feeds ("Betfair Exchange (Lay)", "betfair_exchange_lay_"): the price to lay, not to back. */
export function isLayBookName(bookName = '') {
    return /\(\s*lay\s*\)|(^|[_\s])lay_?$/i.test(String(bookName || '').trim());
}

function getBestPrice(raw, bookOdds) {
    const bo = bookOdds || raw?.bookOdds || {};
    const prices = Object.entries(bo)
        .filter(([b, o]) => !isUnbackableBookData(o, b))
        .map(([, o]) => extractPrice(o))
        .filter((p) => p !== null && typeof p === 'number');
    if (prices.length === 0) return raw?.price ?? raw?.american_price ?? null;
    return prices.reduce((best, p) => (best === null || p > best ? p : best), null);
}

/** Locate a bookOdds entry by American price (stamp-first SGP math block). */
function findBookDataByPrice(raw, bookPrice) {
    const bo = raw?.bookOdds;
    if (!bo || typeof bookPrice !== 'number') return null;
    for (const bd of Object.values(bo)) {
        if (extractPrice(bd) === bookPrice) return bd;
    }
    return null;
}

/**
 * Worst-case EV across all three devig methods (additive, multiplicative,
 * power). All three are CNM-verified; we return the minimum so the single
 * number the sidebar/sort/pill shows equals the ✓ Used row in the card's
 * math block (whichever method is most conservative for this specific
 * market). Matches functions/evCleanPerBook.js backend.
 *
 * ⭐ ADDITIVE POLICY (v1.18 P1 — mirrors functions/sgpOptimizer.js scoreSGP,
 * do not change one side alone):
 *   · PRODUCT-LEVEL min-set includes additive on EVERY user-facing surface:
 *     pill, sort, rail, BetCardV2 — all min(add, mult, pow) of the method
 *     stamps (here + cleanPerBookEv). Backend generation ranking (`_cleanEv`
 *     in sgpOptimizer STEP 5) uses the same min-of-three.
 *   · PER-LEG worst-case inside scoreSGP (fair_probability / ev_percent)
 *     uses min(mult, pow) per CNM — per-leg additive craters on longshot
 *     legs. Those stamps are internal quality metrics, never a display axis.
 */
function wcEvFromMethods(m1, m2, m3, _isSGP, { estimatedVig = false, powerFloor = estimatedVig, additiveVeto = false } = {}) {
    const evs = [];
    // Additive is unreliable when the vig was assumed (betMath.isAdditiveReliable);
    // on a rejected pair it is a veto (floored at 0%), like power.
    if (m1 != null && !estimatedVig) evs.push(m1);
    else if (m1 != null && additiveVeto) {
        const v1 = betMath.powerEvForMinSet(m1, { estimatedVig: true, multiplicativeEv: m2 });
        if (v1 != null) evs.push(v1);
    }
    if (m2 != null) evs.push(m2);
    // Under an assumed vig power is a veto only: its EV is floored at 0%
    // (betMath.powerEvForMinSet, same rule as the server). Two-sided: no-op.
    const p3 = m3 != null
        ? betMath.powerEvForMinSet(m3, { estimatedVig: powerFloor, multiplicativeEv: m2 })
        : m3;
    if (p3 != null) evs.push(p3);
    if (evs.length === 0) return null;
    return Math.min(...evs);
}

/** Additive-exclusion and power-floor flags for a devig_methods block (server parity). */
function _vigFlags(dm) {
    return {
        estimatedVig: dm?.devig_source === 'estimated',
        powerFloor: betMath.isAssumedVigConsensus(dm),
        additiveVeto: dm?.devig_source === 'estimated' && dm?.rejected_pair === true,
    };
}

/**
 * Check whether a book's stored per-book devig data is trustworthy. Backend
 * cache currently has ~60% of book entries with corrupt per-book values
 * (e.g. a book stored opposite_american_odds = -99900, which produces
 * total_implied > 1.25 and clamps all three methods to the same floor).
 * We detect the corruption signatures and reject the stored values so the
 * frontend can fall back to top-level consensus × that book's price.
 */
function isPerBookDevigTrustworthy(bd) {
    if (!bd || typeof bd !== 'object') return false;
    const dm = bd.devig_methods;
    if (!dm) return false;
    const ti = dm.total_implied;
    if (ti != null && (ti > 1.25 || ti < 0.5)) return false; // implausible vig
    const m1 = bd.ev_method1_additive;
    const m2 = bd.ev_method2_multiplicative;
    const m3 = bd.ev_method3_power;
    if (m1 != null && m2 != null && m3 != null && m1 === m2 && m2 === m3 && Math.abs(m1) >= 20) return false;
    return true;
}

/**
 * Signals whether the top-level consensus devig has enough integrity to use.
 * Only rejects extreme garbage: consensus fair vs best-book devig diverging
 * by >25pp (clear data error). `total_implied` is NOT a reliability signal —
 * a sharp book with ti<0.85 is not unreliable, it's just sharp.
 */
export function isTopLevelDevigReliable(raw) {
    const dm = raw?.devig_methods;
    if (!dm) return false;
    const cf = dm.consensus_fair_mult;
    const pf = dm.multiplicative_prob;
    if (cf != null && pf != null && Math.abs(cf - pf) > 0.25) return false;
    return true;
}

/**
 * Pull the book-agnostic consensus fair probabilities from the top-level raw
 * row. Prefers `consensus_fair_*` (pure peer average across all canonical
 * books) over `*_prob` (which was computed vs the best-priced book's implied
 * prob and is therefore book-specific). Returns null if neither is available.
 *
 * NOTE: ti / reliability is not gated here anymore — the caller applies a
 * ±100% cap that handles noise without silently falling back to broken stored
 * per-book `ev_percent` numbers (which caused card-vs-panel EV disagreement).
 */
export function getTopLevelConsensusFairProbs(raw) {
    const dm = raw?.devig_methods;
    if (!dm) return null;
    if (!isTopLevelDevigReliable(raw)) return null;
    return {
        additive:       dm.consensus_fair_add  ?? dm.additive_prob       ?? null,
        multiplicative: dm.consensus_fair_mult ?? dm.multiplicative_prob ?? null,
        power:          dm.consensus_fair_pow  ?? dm.power_prob          ?? null,
    };
}

// v1.15.x P3.3 — Frontend-side devig helpers that mirror
// functions/stages/consensusFilter.js::buildConsensusDevigPayload so a user-
// customized exclusion set can recompute consensus_fair_* without a backend
// round-trip. Kept intentionally small (pure functions, no module state).
const IMP_CLAMP_LO = 0.001;
const IMP_CLAMP_HI = 0.999;
function devigAdditiveFront(p, ti) {
    return Math.max(IMP_CLAMP_LO, Math.min(IMP_CLAMP_HI, p - (ti - 1) / 2));
}
function devigMultiplicativeFront(p, ti) {
    return Math.max(IMP_CLAMP_LO, Math.min(IMP_CLAMP_HI, p / ti));
}
function devigPowerFront(p, ti) {
    if (!Number.isFinite(p) || !Number.isFinite(ti)) return null;
    if (p <= 0 || p >= 1 || ti <= 0) return null;
    const p2 = ti - p;
    if (p2 <= 0 || p2 >= 1) return Math.max(IMP_CLAMP_LO, Math.min(IMP_CLAMP_HI, p / ti));
    let lo = 0.01;
    let hi = 20;
    for (let i = 0; i < 64; i++) {
        const mid = (lo + hi) / 2;
        if (Math.pow(p, 1 / mid) + Math.pow(p2, 1 / mid) > 1) hi = mid;
        else lo = mid;
    }
    const fp = Math.pow(p, 2 / (lo + hi));
    if (!Number.isFinite(fp) || fp <= 0 || fp >= 1) {
        return Math.max(IMP_CLAMP_LO, Math.min(IMP_CLAMP_HI, p / ti));
    }
    return fp;
}

function medianOfFinite(values) {
    const finite = values.filter((v) => Number.isFinite(v));
    if (finite.length === 0) return null;
    const sorted = finite.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Resolve consensus fair probabilities for a row, honoring a user-defined
 * exclusion set. Falls back to backend-stamped `consensus_fair_*` whenever
 * the user's policy is 'default' OR `_rawBookConsensus` is missing/empty
 * (legacy rows, SGPs, cached rows that predate v1.15.x P3).
 *
 * @param {object} raw - option.raw (or similar) carrying devig_methods + _rawBookConsensus
 * @param {object} [opts]
 * @param {'default'|'include_all'|'custom'} [opts.policy='default']
 * @param {Set<string>|string[]} [opts.excluded] - normalized book keys to exclude
 *     (only consulted when policy === 'custom'; 'include_all' ignores both
 *     backend defaults and this set, 'default' uses backend stamps as-is)
 * @returns {{ additive, multiplicative, power, source, bookCount } | null}
 *   source: 'backend_default' | 'frontend_custom' | 'frontend_include_all'
 */
export function resolveConsensusFair(raw, opts = {}) {
    const policy = opts.policy || 'default';
    const backend = getTopLevelConsensusFairProbs(raw);

    if (policy === 'default') {
        if (!backend) return null;
        return {
            ...backend,
            source: 'backend_default',
            bookCount: raw?.devig_methods?.consensus_book_count ?? null,
        };
    }

    const rawBooks = Array.isArray(raw?._rawBookConsensus) ? raw._rawBookConsensus : null;
    if (!rawBooks || rawBooks.length === 0) {
        // No raw stamps — policy cannot be honored locally; fall back to backend.
        return backend
            ? { ...backend, source: 'backend_default', bookCount: raw?.devig_methods?.consensus_book_count ?? null }
            : null;
    }

    const excluded = opts.excluded instanceof Set
        ? opts.excluded
        : new Set((opts.excluded || []).map((s) => String(s).toLowerCase()));

    const kept = rawBooks.filter((r) => {
        if (!r || !r.book) return false;
        if (policy === 'include_all') return true; // ignores excluded
        if (policy === 'custom' && excluded.has(String(r.book).toLowerCase())) return false;
        return true;
    });
    if (kept.length < 1) {
        return backend
            ? { ...backend, source: 'backend_default', bookCount: raw?.devig_methods?.consensus_book_count ?? null }
            : null;
    }

    const myMed = medianOfFinite(kept.map((r) => r.my));
    const oppMed = medianOfFinite(kept.map((r) => r.opp).filter((v) => Number.isFinite(v)));
    if (!Number.isFinite(myMed) || myMed <= 0 || myMed >= 1) {
        return backend
            ? { ...backend, source: 'backend_default', bookCount: raw?.devig_methods?.consensus_book_count ?? null }
            : null;
    }

    const hasOpp = Number.isFinite(oppMed) && oppMed > 0 && oppMed < 1;
    const rawTi = hasOpp ? myMed + oppMed : myMed + (1.10 - myMed);
    const ti = (rawTi >= 0.5 && rawTi <= 1.5) ? rawTi : 1.10;

    const fpAdd = devigAdditiveFront(myMed, ti);
    const fpMul = devigMultiplicativeFront(myMed, ti);
    const fpPow = devigPowerFront(myMed, ti) ?? fpMul;

    return {
        additive: fpAdd,
        multiplicative: fpMul,
        power: fpPow,
        source: policy === 'include_all' ? 'frontend_include_all' : 'frontend_custom',
        bookCount: kept.length,
        // Decided here, after the user's exclusions: no opposite left, or a
        // pair outside the trust band → the vig is assumed (server rule).
        assumedVig: !hasOpp || ti !== rawTi,
    };
}

/**
 * Most conservative consensus fair prob for a price, from a book's
 * devig_methods (else the item's) — the card's live fallback when no
 * backend stamp exists. Same rules as functions/evCleanPerBook
 * pickConservativeFair: additive out under an assumed vig, and power floored
 * at the offered price's implied prob (betMath.powerFairForMinSet, Oct 17
 * 2026 one-sided longshot fix). `samebookFallback` also reads the item's
 * same-book *_prob fields when it has no consensus_fair_*.
 */
export function liveConsensusFairAtPrice(bookDm, itemDm, price, { samebookFallback = false } = {}) {
    // 1 / decimal, exactly like the server (americanToImplied can differ by 1 ulp).
    const _dec = betMath.americanToDecimal(typeof price === 'number' ? price : Number(price));
    const offeredImplied = Number.isFinite(_dec) && _dec > 1 ? 1 / _dec : null;
    const pick = (dm, addKey, mulKey, powKey) => {
        if (!dm) return [];
        // Additive out on any 'estimated' block (as before); the power floor
        // only on an assumed-vig consensus (not a stamped two-sided peer one).
        const est = dm.devig_source === 'estimated';
        const floor = addKey === 'consensus_fair_add' ? betMath.isAssumedVigConsensus(dm) : est;
        const out = [];
        if (Number.isFinite(dm[addKey]) && !est) out.push(dm[addKey]);
        else if (Number.isFinite(dm[addKey]) && dm.rejected_pair === true) {
            const a = betMath.powerFairForMinSet(dm[addKey], { estimatedVig: true, multiplicativeFair: dm[mulKey], offeredImplied });
            if (Number.isFinite(a)) out.push(a);
        }
        if (Number.isFinite(dm[mulKey])) out.push(dm[mulKey]);
        const pow = betMath.powerFairForMinSet(dm[powKey], {
            estimatedVig: floor, multiplicativeFair: dm[mulKey], offeredImplied,
        });
        if (Number.isFinite(pow)) out.push(pow);
        return out;
    };
    const C = ['consensus_fair_add', 'consensus_fair_mult', 'consensus_fair_pow'];
    let c = pick(bookDm, ...C);
    if (c.length === 0) c = pick(itemDm, ...C);
    if (c.length === 0 && samebookFallback) c = pick(itemDm, 'additive_prob', 'multiplicative_prob', 'power_prob');
    return c.length ? Math.min(...c) : null;
}

/**
 * The card math block's "✓ Used" method, on the same axis as the pill.
 * Two-sided: min(additive [not for SGPs], multiplicative, power) — unchanged.
 * Assumed vig (`estimatedVig`): additive is out and power is a veto only, its
 * EV floored at 0% (betMath.powerEvForMinSet), as on the server.
 *
 * @param {{additive?:{ev}, multiplicative?:{ev}, power?:{ev}}} methods
 * @returns {{ key: string, ev: number, powerVetoed: boolean } | null}
 */
export function pickUsedDevigMethod(methods, { isSGP = false, estimatedVig = false, additiveVeto = false } = {}) {
    if (!methods) return null;
    const addRaw = methods.additive?.ev;
    // Rejected pair (stamped `vetoOnly` / option): additive floored at 0% like power.
    const addIsVeto = estimatedVig && (additiveVeto || methods.additive?.vetoOnly === true);
    const add = addIsVeto && addRaw != null
        ? betMath.powerEvForMinSet(addRaw, { estimatedVig: true, multiplicativeEv: methods.multiplicative?.ev })
        : addRaw;
    const mul = methods.multiplicative?.ev;
    const powRaw = methods.power?.ev;
    const pow = powRaw != null
        ? betMath.powerEvForMinSet(powRaw, { estimatedVig, multiplicativeEv: mul })
        : null;
    const c = [
        ...(!isSGP && (!estimatedVig || addIsVeto) && add != null ? [{ key: 'additive', ev: add }] : []),
        ...(mul != null ? [{ key: 'multiplicative', ev: mul }] : []),
        ...(pow != null ? [{ key: 'power', ev: pow }] : []),
    ];
    if (c.length === 0) return null;
    const best = c.reduce((a, b) => (b.ev < a.ev ? b : a));
    return { key: best.key, ev: best.ev, powerVetoed: pow != null && pow !== powRaw };
}

/** American odds → decimal odds (delegates to betMath core). */
function americanToDecimal(price) {
    return betMath.americanToDecimal(price);
}

/** EV% from fair probability × book decimal. */
function evFromFair(fairProb, decimalOdds) {
    if (!Number.isFinite(fairProb) || decimalOdds == null) return null;
    return betMath.evPercent(fairProb, decimalOdds);
}

/**
 * Compute a book's EV from top-level consensus fair probs × book decimal.
 * Returns { additive, multiplicative, power } EV percentages. Same fair prob
 * applied to every book — EV ordering reduces to price ordering.
 *
 * v1.15.x P3.3 — `consensusOptions` (if present) threads a user-chosen policy
 * + excluded-book set through to `resolveConsensusFair`, overriding the
 * backend-stamped consensus when the user opted into `include_all` / `custom`.
 * Callers that don't pass options get the backend-stamped consensus (old
 * behaviour, zero-regression default path).
 */
export function computeBookEvFromConsensus(raw, bookPrice, consensusOptions) {
    const consensus = consensusOptions
        ? resolveConsensusFair(raw, consensusOptions)
        : getTopLevelConsensusFairProbs(raw);
    if (!consensus) return null;
    const dec = americanToDecimal(bookPrice);
    if (!dec) return null;
    const calc = (fp) => {
        const ev = evFromFair(fp, dec);
        return ev != null ? parseFloat(ev.toFixed(2)) : null;
    };
    const out = {
        additive:       calc(consensus.additive),
        multiplicative: calc(consensus.multiplicative),
        power:          calc(consensus.power),
    };
    // Locally recomputed consensus (custom / include_all) carries its own
    // assumed-vig verdict; not enumerable so callers spreading it see 3 keys.
    if (typeof consensus.assumedVig === 'boolean') {
        Object.defineProperty(out, 'assumedVig', { value: consensus.assumedVig, enumerable: false });
    }
    return out;
}

/**
 * Per-book EV for an SGP item at a given book's American price — THE canonical
 * implementation of the card-display formula:
 *
 *   fp_X = (ev_method_X / 100 + 1) / combined_dec      (X ∈ additive, mult, power)
 *   ev_at_book_X = (fp_X × book_dec − 1) × 100
 *   result = min(ev1, ev2, ev3)
 *
 * Additive is INCLUDED per client preference — extra conservatism on
 * extreme-correlation SGPs is desirable.
 *
 * History: this formula was hand-copied in three places (UnifiedBetCard.perBookEV
 * SGP branch, SgpFlowScreen._cardEvForSgp ×2) with "must match line-for-line"
 * comments. They now all call this one function, so the sort axis, the on-card
 * pill, and the debug dump can never disagree again.
 *
 * Returns the worst-case (min) EV as a number, or null when the item lacks a
 * numeric combined_price / book price / any method stamps.
 */
export function sgpEvAtBookPrice(raw, bookPrice) {
    if (!raw || typeof bookPrice !== 'number') return null;
    // Stamp-frame re-price only (no leave-one-out): same raw stamps and cap as
    // the backend via betMath.sgpStampInput.
    const input = betMath.sgpStampInput(raw, null, bookPrice);
    return betMath.sgpPerBookEvFromStamps({
        ...input,
        combinedPrice: raw.combined_price,
        otherBookDecimals: [],
    });
}

/**
 * Math-block snapshot for straight bets — reads backend-stamped method_rows.
 */
export function straightDevigMethodsSnapshot(bookData, consensusOptions) {
    const hasActivePolicy = consensusOptions?.policy && consensusOptions.policy !== 'default';
    if (hasActivePolicy || !bookData?.method_rows) return null;
    const rows = bookData.method_rows;
    return {
        additive: rows.additive ?? null,
        multiplicative: rows.multiplicative ?? null,
        power: rows.power ?? null,
        methodUsed: rows.methodUsed || 'power',
        isSGP: false,
        _fromStamp: true,
    };
}

/**
 * Stamp-first (v1.18 P3): reads `bookOdds[b].sgp_method_rows` stamped by
 * annotateCleanEv when present; recomputes only as fallback.
 */
export function sgpDevigMethodsSnapshot(raw, bookPrice, consensusOptions, bookData, bookName = null) {
    if (!raw || typeof bookPrice !== 'number' || typeof raw.combined_price !== 'number') return null;

    const hasActivePolicy = consensusOptions?.policy && consensusOptions.policy !== 'default';
    const stampedRows = !hasActivePolicy
        ? (bookData?.sgp_method_rows ?? findBookDataByPrice(raw, bookPrice)?.sgp_method_rows)
        : null;
    if (stampedRows) {
        return {
            additive: stampedRows.additive ?? null,
            multiplicative: stampedRows.multiplicative ?? null,
            power: stampedRows.power ?? null,
            methodUsed: 'individual_leg_devig',
            isSGP: true,
            _isCachedSGP: true,
            looApplied: stampedRows.looApplied === true,
            rExclRatio: stampedRows.rExclRatio,
            _fromStamp: true,
        };
    }

    // Same builder as the backend (evCleanPerBook): mainstream-only LOO set,
    // no LOO under 2 consensus books, raw stamps, stored cap.
    const rows = betMath.sgpMethodRowsFromStamps({
        ...betMath.sgpStampInput(raw, bookName ?? bookNameForData(raw, bookData), bookPrice, consensusOptions),
        combinedPrice: raw.combined_price,
    });
    if (!rows) return null;

    return {
        additive: rows.additive,
        multiplicative: rows.multiplicative,
        power: rows.power,
        methodUsed: 'individual_leg_devig',
        isSGP: true,
        _isCachedSGP: true,
        looApplied: rows.looApplied === true,
        rExclRatio: rows.rExclRatio,
    };
}

/**
 * Thin consensus (Nov 3 2026): this SGP's EV at `bookName` is measured against
 * only one other fair-value book (e.g. DK +270 priced against FD +150 alone).
 * Same leave-one-out set as the EV itself (betMath.sgpStampInput), so a
 * 'custom' user's fair-value books count, and a mirror feed is not a second book.
 * The math is unchanged (25% cap); this only labels it.
 */
export function sgpThinConsensus(raw, bookName, bookPrice, consensusOptions) {
    if (!raw || raw._noEv === true || typeof bookPrice !== 'number') return false;
    if (raw.ev_method !== 'individual_leg_devig' && raw._isCachedSGP !== true && raw.type !== 'sgp') return false;
    const input = betMath.sgpStampInput(raw, bookName || null, bookPrice, consensusOptions);
    return !!input && Array.isArray(input.otherBookDecimals) && input.otherBookDecimals.length === 1;
}

// v1.17.13 — EV cap removed. Genuine high-EV lines show real values.
// SORT_EV_MAX kept as an exported reference for any callers that still reference it.
const SORT_EV_MAX = Infinity;
const SORT_EV_MIN = -Infinity;

/**
 * Clean per-book EV — stamp-first display path.
 *
 * Priority:
 *   0. Backend-stamped bookOdds[b].clean_ev_percent (annotateCleanEv at serve
 *      time). When present and policy is default, this is authoritative — the
 *      frontend does NOT recompute SGP LOO math.
 *   0b. SGP recompute fallback — sgpPerBookEvFromStamps when stamp missing
 *      (stale cache, pre-annotate rows, dev-only fixtures).
 *   1. Top-level consensus_fair × book decimal (custom consensus policy or
 *      when stamp absent).
 *   2. Per-book stored ev_method* IF trustworthy.
 *   3. Per-book ev_percent fallback.
 */
export function cleanPerBookEv(raw, bookData, isSGP, consensusOptions, bookName = null) {
    if (!bookData) return null;
    // Server-marked no-EV row (functions/utils/noEvRows.js): no EV is shown or
    // recomputed from leftover stamps; it renders price-only with "No EV".
    if (raw?._noEv === true) return null;
    const bookPrice = typeof bookData === 'object' ? bookData.price : bookData;

    const applyCap = (v) => {
        if (v == null || !Number.isFinite(v)) return null;
        return betMath.displayEvCap(v, betMath.isPlayerPropItem(raw));
    };
    const _isLegacyCap = (v) => Math.abs(v - 40) < 0.02 || Math.abs(v - 30) < 0.02;
    const hasActivePolicy = consensusOptions
        && consensusOptions.policy
        && consensusOptions.policy !== 'default';
    const isSGPItem = isSGP || raw?.ev_method === 'individual_leg_devig';

    // Priority 0: backend-stamped clean EV — compute once on the server.
    if (!hasActivePolicy && typeof bookData === 'object' && Number.isFinite(bookData.clean_ev_percent)
        && !_isLegacyCap(bookData.clean_ev_percent)) {
        return applyCap(bookData.clean_ev_percent);
    }

    // Priority 0b: SGP fallback — recompute only when the stamp is absent or
    // the user opted into a custom consensus exclusion policy.
    if (
        isSGPItem &&
        typeof bookPrice === 'number' &&
        typeof raw?.combined_price === 'number' &&
        (
            Number.isFinite(raw?.ev_method1_additive) ||
            Number.isFinite(raw?.ev_method2_multiplicative) ||
            Number.isFinite(raw?.ev_method3_power)
        )
    ) {
        // Same builder as the backend (evCleanPerBook): mainstream-only LOO
        // set, no LOO under 2 consensus books, raw stamps, stored cap. A
        // 'custom' policy re-derives R against the user's fair-value books.
        const sgpEv = betMath.sgpPerBookEvFromStamps({
            ...betMath.sgpStampInput(raw, bookName ?? bookNameForData(raw, bookData), bookPrice, consensusOptions),
            combinedPrice: raw.combined_price,
        });
        if (Number.isFinite(sgpEv)) return applyCap(sgpEv);
    }

    // Priority 1: top-level consensus × book decimal (only when reliable)
    const evs = computeBookEvFromConsensus(raw, bookPrice, consensusOptions);
    if (evs) {
        const wc = wcEvFromMethods(evs.additive, evs.multiplicative, evs.power, isSGP,
            typeof evs.assumedVig === 'boolean'
                ? { estimatedVig: evs.assumedVig, powerFloor: evs.assumedVig }
                : _vigFlags(raw.devig_methods));
        if (wc != null) return applyCap(wc);
    }

    // Priority 2: trusted per-book stored values
    if (typeof bookData === 'object' && isPerBookDevigTrustworthy(bookData)) {
        const wc = wcEvFromMethods(
            bookData.ev_method1_additive,
            bookData.ev_method2_multiplicative,
            bookData.ev_method3_power,
            isSGP,
            _vigFlags(bookData.devig_methods)
        );
        if (wc != null) return applyCap(wc);
    }

    // Priority 3: stored ev_percent (backend's best effort even when devig was noisy)
    if (typeof bookData === 'object' && typeof bookData.ev_percent === 'number') {
        return applyCap(bookData.ev_percent);
    }
    return null;
}

/**
 * Canonical sort EV — the best EV this bet offers across all its books.
 * With consensus × decimal logic, this reduces to: pick the best-priced book,
 * compute its EV vs consensus. Auto-select picks that same book, so card
 * badge matches sort order.
 */
export function fallbackEvLikeCardEvMemo(option, consensusOptions) {
    const raw = option?.raw || {};
    if (raw._noEv === true || option?._noEv === true) return -999; // sorts last
    const isSGP = raw.ev_method === 'individual_leg_devig';
    const bookOdds = option?.bookOdds || raw.bookOdds || {};

    let bestWc = null;
    Object.entries(bookOdds).forEach(([book, bd]) => {
        if (isUnbackableBookData(bd, book)) return;
        const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, book);
        if (wc == null) return;
        if (bestWc == null || wc > bestWc) bestWc = wc;
    });
    if (bestWc != null) return bestWc;

    // Ultimate fallback: top-level ev_method* (no bookOdds or legacy SGP shape)
    const wc = wcEvFromMethods(raw.ev_method1_additive, raw.ev_method2_multiplicative, raw.ev_method3_power, isSGP,
        _vigFlags(raw.devig_methods));
    if (wc != null) return wc;

    const v = raw.ev_percent ?? option?.ev_percent ?? null;
    return typeof v === 'number' ? v : -999;
}

/**
 * @param {object} option - Leg/row option (workingOption shape)
 * @param {object[]} marketPeers - Opposite-side rows for same market key
 * @param {number|null|undefined} priceOverride - American odds for devig math (card: current book price). Omit for sort (best price).
 * @param {object|null|undefined} bookData - Optional bookOdds entry; when stamped method_rows exist, read instead of recompute.
 */
export function computeDevigMethodsSnapshot(option, marketPeers = [], priceOverride, bookData) {
    const raw = option?.raw || {};
    const workingOption = option;
    const bookOdds = workingOption?.bookOdds || raw.bookOdds || {};
    const price =
        priceOverride !== undefined && priceOverride !== null
            ? priceOverride
            : getBestPrice(raw, bookOdds);

    const resolvedBookData = bookData ?? findBookDataByPrice(raw, price);
    const stampedStraight = straightDevigMethodsSnapshot(resolvedBookData, null);
    if (stampedStraight) return stampedStraight;

    const implied = betMath.americanToImplied(price);

    // Priority 1a: top-level devig_methods (used when no per-book devig data is available,
    // e.g. SGP cached shape or legacy cache entries). With per-book leave-one-out devig now
    // flowing through teamCacheFastPath/playerFastPath/bestBetsFastPath/etc., straight bets
    // should generally hit UnifiedBetCard's Priority 1/2 path that reads bookOdds[book]
    // directly — we only reach here as a fallback.
    if (
        price != null &&
        raw.devig_methods &&
        raw.ev_method1_additive !== null &&
        raw.ev_method1_additive !== undefined
    ) {
        const dec = americanToDecimal(price);
        if (!dec) return null;
        const addFP = raw.devig_methods.additive_prob;
        const mulFP = raw.devig_methods.multiplicative_prob;
        const powFP = raw.devig_methods.power_prob;
        const calcEV = (fp) => evFromFair(fp, dec);
        return {
            additive: { fairProb: addFP, ev: calcEV(addFP) ?? raw.ev_method1_additive },
            multiplicative: { fairProb: mulFP, ev: calcEV(mulFP) ?? raw.ev_method2_multiplicative },
            power:
                powFP != null
                    ? { fairProb: powFP, ev: calcEV(powFP) }
                    : raw.ev_method3_power != null
                      ? { fairProb: null, ev: raw.ev_method3_power }
                      : null,
            methodUsed: raw.ev_method_used || 'power',
            isSGP: false,
        };
    }

    // Priority 1b SGP
    if (
        raw.ev_method === 'individual_leg_devig' &&
        raw.ev_method2_multiplicative !== null &&
        raw.ev_method2_multiplicative !== undefined
    ) {
        const fp = raw.fair_probability != null ? raw.fair_probability / 100 : null;
        const dec = americanToDecimal(price);
        const recalc = (backendEV) => (fp && dec ? evFromFair(fp, dec) : backendEV);
        return {
            additive:
                raw.ev_method1_additive != null
                    ? { fairProb: fp, ev: recalc(raw.ev_method1_additive) }
                    : null,
            multiplicative:
                raw.ev_method2_multiplicative != null
                    ? { fairProb: fp, ev: recalc(raw.ev_method2_multiplicative) }
                    : null,
            power:
                raw.ev_method3_power != null
                    ? { fairProb: fp, ev: recalc(raw.ev_method3_power) }
                    : null,
            methodUsed: 'individual_leg_devig',
            isSGP: true,
        };
    }

    if (implied === null || price === null) return null;

    const decimalOdds = americanToDecimal(price);
    if (!decimalOdds) return null;
    const impliedProb = implied;

    const toImpliedProb = (american) => betMath.americanToImplied(american);
    const getAllPrices = (bo) => {
        if (!bo || typeof bo !== 'object') return [];
        return Object.values(bo)
            .map((o) => extractPrice(o))
            .filter((p) => p !== null && !isNaN(p));
    };

    let totalImplied;
    let oppositeImplied;

    // ⭐ Priority 1: peer-based implied averaging (fresh market data, outlier-resistant).
    // This is the cleanest source — it averages the market's actual quoted prices across
    // all books on both sides in implied-probability space. Always prefer this over
    // working backwards from a stored ev_percent (which may be stale).
    const currentSelection = (
        workingOption?.selection ||
        raw.selection ||
        ''
    ).toLowerCase();
    const oppositeOption = marketPeers.find((peer) => {
        const peerSelection = (peer?.selection || peer?.raw?.selection || '').toLowerCase();
        return peerSelection !== currentSelection && peerSelection.length > 0;
    });

    if (oppositeOption && oppositeOption.bookOdds) {
        const oppPrices = getAllPrices(oppositeOption.bookOdds);
        const currentPrices = getAllPrices(workingOption?.bookOdds || raw.bookOdds);
        if (oppPrices.length > 0 && currentPrices.length > 0) {
            const avgCurrentImplied =
                currentPrices.map(toImpliedProb).reduce((s, p) => s + p, 0) /
                currentPrices.length;
            const avgOppImplied =
                oppPrices.map(toImpliedProb).reduce((s, p) => s + p, 0) / oppPrices.length;
            totalImplied = avgCurrentImplied + avgOppImplied;
            oppositeImplied = avgOppImplied;
        }
    }

    // ⭐ Priority 2: estimate total implied from stored ev_percent ONLY when no
    // peers are available. We no longer let this override the peer-derived totalImplied
    // and we never let stale backendEV replace the freshly computed EV outputs below.
    if (!totalImplied || totalImplied <= 1) {
        const backendEV = raw.ev_percent ?? workingOption?.ev_percent ?? null;
        if (backendEV !== null && backendEV !== undefined && Math.abs(backendEV) < 200) {
            const backendFairProb = Math.max(
                0.01,
                Math.min(0.99, (backendEV / 100 + 1) / decimalOdds)
            );
            const vigEstimate =
                impliedProb > 0.01
                    ? (impliedProb / backendFairProb - 1) / Math.max(0.01, 1 - impliedProb)
                    : 0.1;
            const vigClamped = Math.max(0.001, Math.min(0.3, vigEstimate));
            totalImplied = 1 + vigClamped;
            oppositeImplied = Math.max(0.01, Math.min(0.99, totalImplied - impliedProb));
        }
    }

    // Priority 3: flat 10% juice assumption when nothing else is available
    if (!totalImplied || totalImplied <= 1) {
        const assumedTotalVig = 0.1;
        totalImplied = 1 + assumedTotalVig;
        oppositeImplied = Math.max(0.01, Math.min(0.99, totalImplied - impliedProb));
    }

    const fairProb_additive = Math.max(
        0.001,
        Math.min(0.999, impliedProb - (totalImplied - 1) / 2)
    );
    const ev_additive = (fairProb_additive * decimalOdds - 1) * 100;

    const fairProb_multiplicative = impliedProb / totalImplied;
    const ev_multiplicative = (fairProb_multiplicative * decimalOdds - 1) * 100;

    let fairProb_power = impliedProb / totalImplied;
    if (oppositeImplied > 0.001 && oppositeImplied < 0.999 && totalImplied > 1) {
        let lo = 0.01;
        let hi = 20;
        for (let i = 0; i < 64; i++) {
            const mid = (lo + hi) / 2;
            if (
                Math.pow(impliedProb, 1 / mid) + Math.pow(oppositeImplied, 1 / mid) >
                1
            )
                hi = mid;
            else lo = mid;
        }
        fairProb_power = Math.pow(impliedProb, 2 / (lo + hi));
    }
    // ⭐ Always use the freshly-computed EV values — never substitute stale stored
    // ev_percent as the final power EV output. The old code used backendEV as the
    // "power" result when within ±200%, which caused stale cached values to bleed
    // through even after fresh peer-based totalImplied was computed above.
    const ev_power = (fairProb_power * decimalOdds - 1) * 100;

    return {
        additive: { fairProb: fairProb_additive, ev: ev_additive },
        multiplicative: { fairProb: fairProb_multiplicative, ev: ev_multiplicative },
        power: { fairProb: fairProb_power, ev: ev_power },
        methodUsed: 'power',
        isSGP: false,
    };
}

/** Single number for sorting: same as badge EV line (min of method EVs, SGP excludes additive from min set).
 *
 * v1.15.x P3.3 — accepts an optional `consensusOptions = { policy, excluded }`
 * so the results list can sort under the user's chosen exclusion policy. When
 * omitted, behaviour matches the pre-P3 default (backend consensus).
 */
export function getCanonicalSortEv(option, peersMap, consensusOptions) {
    const raw = option?.raw || {};
    const isSGPRaw = raw.ev_method === 'individual_leg_devig';

    // ⭐ Both straights AND SGPs now route through fallbackEvLikeCardEvMemo.
    // It iterates bookOdds and delegates per-book EV to cleanPerBookEv, which
    // (post-v1.13.x) handles SGPs correctly by recomputing each book's EV
    // from item-level method stamps (ev_method2_multiplicative / ev_method3_power)
    // rather than the stored bookOdds[b].ev_percent (composed via per-leg min
    // fair-prob product, which is over-conservative and caused sort≠pill).
    //
    // Previously the SGP branch called computeDevigMethodsSnapshot which uses
    // `raw.fair_probability` — that field is the over-conservative product and
    // returns the same wrong number for both `multiplicative` and `power`. The
    // result was a sort axis that disagreed with the pill for SGPs whenever
    // per-leg mins alternated methods across legs. Unifying both branches on
    // fallbackEvLikeCardEvMemo keeps the axis glued to the same math the card
    // renders for both straights and SGPs.
    return fallbackEvLikeCardEvMemo(option, consensusOptions);
}
